"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = updatePrintableLayerUserData;
const EditableTextLayer_1 = require("../../models/layers/EditableTextLayer");
const UserPhotoLayer_1 = require("../../models/layers/UserPhotoLayer");
const EditableDesignAssetLayer_1 = require("../../models/layers/EditableDesignAssetLayer");
const EditableTextLayerUserData_1 = require("../../models/layers/EditableTextLayerUserData");
const DesignAssetLayer_1 = require("../../models/layers/DesignAssetLayer");
const Printable_1 = require("../../models/printables/Printable");
const either_1 = require("../../types/either");
const updatePrintableGroupQuantityAction_1 = __importDefault(require("./updatePrintableGroupQuantityAction"));
const targetedLayerMatchesUpdate = (layer, userData) => {
  if (!userData) return true;
  const typeguards = {
    user_photo: UserPhotoLayer_1.isUserPhotoLayerUserData,
    editable_text: EditableTextLayerUserData_1.isEditableTextLayerUserData,
    editable_design_asset: EditableDesignAssetLayer_1.isEditableDesignAssetLayerData,
    design_asset: DesignAssetLayer_1.isDesignAssetLayerData
  };
  return !!layer && typeguards[layer.type] && typeguards[layer.type](userData);
};
function updatePrintableLayerUserData(project, action) {
  var _a, _b, _c, _d, _e, _f, _g;
  const printableGroupIndex = project.printableGroups.findIndex(pg => !!pg.printables.find(p => p.id === action.printableId));
  if (printableGroupIndex === -1) {
    return (0, either_1.left)(new Error(`Could not find printable group  - printableId: ${action.printableId}`));
  }
  const printableIndex = project.printableGroups[printableGroupIndex].printables.findIndex(p => p.id === action.printableId);
  if (printableIndex === -1) {
    return (0, either_1.left)(new Error(`Could not find printable for printableId: ${action.printableId}`));
  }
  const layerIndex = project.printableGroups[printableGroupIndex].printables[printableIndex].layers.findIndex(layer => layer.id === action.layerId);
  if (layerIndex === -1) {
    return (0, either_1.left)(new Error(`Could not find layer for layerId: ${action.layerId}`));
  }
  const printableUpdated = (0, Printable_1.updatePrintableVersion)(project.printableGroups[printableGroupIndex].printables[printableIndex], action.timestamp);
  const targetedLayer = printableUpdated.layers[layerIndex];
  const updateValidTypeForLayer = targetedLayerMatchesUpdate(targetedLayer, action.userData);
  if (!updateValidTypeForLayer) {
    return (0, either_1.left)(new Error(`Targeted layer type does not match update request: layer type: ${targetedLayer.type}, update: ${JSON.stringify(action.userData)}`));
  }
  // This does not handle invalid crops fro custom rotation (EN-6986)
  // The issue fixed here is primarily when pintura produces a max crop for images that are rotated in the workspace
  if ((0, UserPhotoLayer_1.isUserPhotoLayerUserData)(action.userData) && !(0, UserPhotoLayer_1.isUserPhotoLayerUserDataValid)(action.userData)) {
    const modifications = action.userData.modifications;
    let imageWidth = action.userData.imageData.width;
    let imageHeight = action.userData.imageData.height;
    const rotationInDegrees = Math.floor(Math.abs((modifications.rotation || 0) * 180 / Math.PI));
    if (rotationInDegrees && rotationInDegrees % 90 === 0 && rotationInDegrees % 180 !== 0) {
      imageWidth = action.userData.imageData.height;
      imageHeight = action.userData.imageData.width;
    }
    const pxOutOfBoundsX = Math.max((((_a = modifications.crop) === null || _a === void 0 ? void 0 : _a.x) || 0) + (((_b = modifications.crop) === null || _b === void 0 ? void 0 : _b.width) || 0) - imageWidth, 0);
    const pxOutOfBoundsY = Math.max((((_c = modifications.crop) === null || _c === void 0 ? void 0 : _c.y) || 0) + (((_d = modifications.crop) === null || _d === void 0 ? void 0 : _d.height) || 0) - imageHeight, 0);
    const potentialXCorrectionInBounds = (((_e = modifications.crop) === null || _e === void 0 ? void 0 : _e.x) || 0) - pxOutOfBoundsX >= 0;
    const potentialYCorrectionInBounds = (((_f = modifications.crop) === null || _f === void 0 ? void 0 : _f.y) || 0) - pxOutOfBoundsY >= 0;
    // Pintura occasionally saves very small rotation amounts (less than 1 degree) on image modifications which result in the canvas rendering the image
    // at a size just slightly bigger than the original image dimensions.  This can result in the crop data being marginally out of bounds of the original
    // dimensions of the image.
    // Here we make sure that we can nudge the crop back in bounds without creating new crop out of bounds errors.  The tolerance of 50px should cover situations
    // where the rotation on the image is negligible and the crop ends up just out of bounds of the original dimensions of the image.
    const correctable = potentialXCorrectionInBounds && potentialYCorrectionInBounds && pxOutOfBoundsX <= 50 && pxOutOfBoundsY <= 50;
    if (window.newrelic) {
      window.newrelic.addPageAction('sdk_invalid_user_photo_layer_data', {
        rotationRadians: modifications.rotation,
        rotationDegrees: rotationInDegrees,
        pxOutOfBoundsX,
        pxOutOfBoundsY,
        imageWidth,
        imageHeight,
        correctable,
        crop: JSON.stringify(modifications.crop)
      });
    }
    if (correctable && modifications.crop) {
      if (pxOutOfBoundsX) {
        modifications.crop.x -= pxOutOfBoundsX;
      }
      if (pxOutOfBoundsY) {
        modifications.crop.y -= pxOutOfBoundsY;
      }
    } else {
      return (0, either_1.left)(new Error(`User photo layer data is invalid: crop is out of bounds, image size ${imageWidth}x${imageHeight}, crop ${JSON.stringify(modifications.crop)}`));
    }
  }
  // @ts-ignore: accessing targeted user_photo layer type
  targetedLayer.userData = action.userData;
  if ((0, EditableTextLayer_1.isEditableTextLayer)(targetedLayer) && ((_g = targetedLayer.userData) === null || _g === void 0 ? void 0 : _g.content)) {
    targetedLayer.userData.assetId = (0, EditableTextLayer_1.generateTextAssetId)(targetedLayer.width, targetedLayer.height, targetedLayer.userData.content, printableUpdated.surface);
  }
  project.printableGroups[printableGroupIndex].printables[printableIndex] = printableUpdated;
  const updatedEntities = [printableUpdated];
  // For print projects, if the user removed an image from a print that has multiple copies,
  // reset the quantity back to 1 (because now it's an empty print)
  const isPrintProject = project.product.hammerCategory === 'prints';
  if (!action.userData && isPrintProject && project.printableGroups[printableGroupIndex].qty > 1) {
    const updatePrintableGroupQuantityResult = (0, updatePrintableGroupQuantityAction_1.default)(project, {
      type: 'UPDATE_PRINTABLE_GROUP_QUANTITY',
      printableGroupId: project.printableGroups[printableGroupIndex].id,
      quantity: 1,
      timestamp: action.timestamp,
      projectId: project.id
    });
    if (updatePrintableGroupQuantityResult.isLeft()) {
      return (0, either_1.left)(updatePrintableGroupQuantityResult.value);
    }
    project = updatePrintableGroupQuantityResult.value;
  }
  project.printableGroups[printableGroupIndex].printables[printableIndex] = printableUpdated;
  updatedEntities.push(project);
  return (0, either_1.right)({
    updatedProject: project,
    updatedEntities: updatedEntities
  });
}